import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/Auth'
import { useClient } from '@/contexts/Services'
import { QUERY_KEYS } from '@/constants/query-keys'

/**
 * Branches the signed-in user is authorized to access, from
 * `GET /me/profile/branches` (QTY-1900). Single source of truth for the
 * sidebar branch selector — the server enforces the filtering, so unauthorized
 * branches never reach client state. Do NOT fall back to the all-branches
 * list here; that would leak branches the user can't access.
 *
 * Cached in memory only, so a fresh page load always reflects current
 * permissions. `useAuth().user` is reactive (Firebase's `auth.currentUser`
 * is not), so the query re-enables correctly on sign-in.
 */
export function useMyBranches() {
  const client = useClient()
  const { user } = useAuth()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [QUERY_KEYS.branches, 'mine'],
    queryFn: () => client!.authentication.getMyBranches(),
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: !!client && !!user,
  })

  return { branches: data ?? [], isLoading, isError, refetch }
}
